export type DistanceUnit = "miles" | "kilometers";

const EARTH_RADIUS_KM = 6371;

/**
 * Calculate the distance between two points using the Haversine formula
 * @returns Distance in kilometers
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

const MILES_LOCALES = ["en-us", "en-gb"];

/**
 * Determine the appropriate distance unit based on locale
 * US and GB use miles, others use kilometers
 */
export function getDistanceUnit(locale: string): DistanceUnit {
  return MILES_LOCALES.includes(locale.toLowerCase()) ? "miles" : "kilometers";
}

/**
 * Format a distance value with the appropriate unit suffix
 * Distances < 10 show one decimal, >= 10 show whole number
 *
 * @param distanceKm in km
 * @param unit DistanceUnit
 *
 * If unit is miles, convert internally.
 */
export function formatDistance(distanceKm: number, unit: DistanceUnit): string {
  const suffix = unit === "miles" ? "mi" : "km";
  const distance = unit === "miles" ? distanceKm * 0.621371 : distanceKm;

  // Round to one decimal first to determine display format
  const rounded = Math.round(distance * 10) / 10;

  if (rounded >= 10) {
    return `${Math.round(distance)} ${suffix}`;
  }

  return `${rounded} ${suffix}`;
}
